import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useT, setStoredLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import {
  FilePlus2, Megaphone, Settings, LogOut, Menu, X, Zap, Mail,
  LayoutDashboard, Building2, Inbox, ChevronRight, ChevronDown, Plus, Pencil, Trash2, Folder, FolderPlus, Layers,
} from "lucide-react";

export default function AppShell({ children }) {
  const { user, logout, refresh } = useAuth();
  const { t, isEn, lang } = useT();
  const navigate = useNavigate();
  const location = useLocation();

  const NAV_ITEMS = [
    { to: "/app", label: t("nav.home"), icon: LayoutDashboard, end: true },
    { to: "/app/uusi", label: t("nav.newReport"), icon: FilePlus2 },
    // Pikaraportti piilotettu navigaatiosta — ohjaa käyttäjät pitkään raportin
    // luontiin. Reitti /app/pikaraportti/* säilyy backendissä legacy-syistä.
    ...(isEn ? [] : [{ to: "/app/asukastiedote/uusi", label: t("nav.newNotice"), icon: Megaphone }]),
    { to: "/app/kohteet", label: t("nav.properties"), icon: Building2 },
    { to: "/app/tyopohjat", label: t("nav.templates"), icon: Layers },
    { to: "/app/asetukset", label: t("nav.settings"), icon: Settings },
  ];
  const [open, setOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [adding, setAdding] = useState(null); // parent_id|"ROOT"|null
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameText, setRenameText] = useState("");

  // Selected folder is reflected by URL ?folder=...
  const params = new URLSearchParams(location.search);
  const selectedId = params.get("folder") || (location.pathname === "/app" ? "ALL" : null);

  const loadFolders = async () => {
    try {
      const { data } = await api.get("/folders");
      setFolders(data.folders || []);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => {
    loadFolders();
    const handler = () => loadFolders();
    window.addEventListener("veraso-folders-refresh", handler);
    return () => window.removeEventListener("veraso-folders-refresh", handler);
  }, []);

  const tree = useMemo(() => {
    const byParent = {};
    for (const f of folders) {
      const p = f.parent_id || "ROOT";
      (byParent[p] ||= []).push(f);
    }
    Object.values(byParent).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name, "fi")));
    return byParent;
  }, [folders]);

  // Expand path to selected
  useEffect(() => {
    if (!selectedId || selectedId === "ALL" || selectedId === "UNFILED") return;
    const byId = Object.fromEntries(folders.map((f) => [f.folder_id, f]));
    const next = { ...expanded };
    let cur = byId[selectedId];
    while (cur?.parent_id) { next[cur.parent_id] = true; cur = byId[cur.parent_id]; }
    setExpanded(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, folders.length]);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const switchLang = async (next) => {
    if (next === lang) return;
    setStoredLang(next);
    try {
      await api.patch("/auth/me", { language: next });
      await refresh();
    } catch {
      toast.error("Language switch failed");
    }
  };

  const goWithFolder = (id) => {
    const target = id && id !== "ALL" ? `/app?folder=${encodeURIComponent(id)}` : "/app";
    navigate(target);
    setOpen(false);
  };

  const submitNew = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/folders", { name, parent_id: adding === "ROOT" ? null : adding });
      setNewName(""); setAdding(null);
      await loadFolders();
      window.dispatchEvent(new CustomEvent("veraso-folders-refresh"));
      toast.success("Kansio luotu");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Luonti epäonnistui");
    }
  };

  const submitRename = async (id) => {
    const name = renameText.trim();
    if (!name) return;
    try {
      await api.patch(`/folders/${id}`, { name });
      setRenaming(null); setRenameText("");
      await loadFolders();
      window.dispatchEvent(new CustomEvent("veraso-folders-refresh"));
    } catch { toast.error("Nimeäminen epäonnistui"); }
  };

  const removeFolder = async (id) => {
    if (!confirm("Poista tämä kansio ja kaikki sen alikansiot? Raportit ja tiedotteet siirtyvät 'Hallinnoimaton'-listalle.")) return;
    try {
      await api.delete(`/folders/${id}`);
      await loadFolders();
      window.dispatchEvent(new CustomEvent("veraso-folders-refresh"));
      if (selectedId === id) goWithFolder("ALL");
      toast.success("Kansio poistettu");
    } catch { toast.error("Poisto epäonnistui"); }
  };

  const renderNode = (folder, depth) => {
    const children = tree[folder.folder_id] || [];
    const isOpen = !!expanded[folder.folder_id];
    const isSelected = selectedId === folder.folder_id;
    const isRenaming = renaming === folder.folder_id;
    return (
      <div key={folder.folder_id} className="select-none" data-testid={`folder-node-${folder.folder_id}`}>
        <div
          className={`group flex items-center gap-1 rounded-lg pr-1 ${isSelected ? "bg-white/95 text-emerald-800" : "text-white/85 hover:bg-white/10"}`}
          style={{ paddingLeft: depth * 10 }}
        >
          <button
            type="button"
            onClick={() => setExpanded((m) => ({ ...m, [folder.folder_id]: !isOpen }))}
            className="h-6 w-5 flex items-center justify-center text-current/70"
            aria-label="toggle"
          >
            {children.length > 0 ? (
              isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
            ) : <span className="h-3.5 w-3.5" />}
          </button>
          <Folder className={`h-3.5 w-3.5 flex-shrink-0 ${isSelected ? "text-emerald-700" : ""}`} />
          {isRenaming ? (
            <input
              autoFocus
              value={renameText}
              onChange={(e) => setRenameText(e.target.value)}
              onBlur={() => submitRename(folder.folder_id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); submitRename(folder.folder_id); }
                if (e.key === "Escape") { setRenaming(null); setRenameText(""); }
              }}
              className="flex-1 min-w-0 px-1.5 py-0.5 text-sm rounded text-slate-900 border border-emerald-300 outline-none"
              data-testid={`folder-rename-input-${folder.folder_id}`}
            />
          ) : (
            <button
              type="button"
              onClick={() => goWithFolder(folder.folder_id)}
              className="flex-1 min-w-0 text-left text-sm py-1 truncate"
              data-testid={`folder-select-${folder.folder_id}`}
            >
              <span className="font-medium truncate">{folder.name}</span>
              {(folder.report_count > 0 || folder.notice_count > 0) && (
                <span className="ml-2 text-[10px] opacity-60">{(folder.report_count || 0) + (folder.notice_count || 0)}</span>
              )}
            </button>
          )}
          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
            <button
              type="button"
              onClick={() => { setAdding(folder.folder_id); setNewName(""); setExpanded((m) => ({ ...m, [folder.folder_id]: true })); }}
              className="p-1 rounded hover:bg-white/15"
              data-testid={`folder-add-child-${folder.folder_id}`}
              title="Uusi alikansio"
            >
              <Plus className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => { setRenaming(folder.folder_id); setRenameText(folder.name); }}
              className="p-1 rounded hover:bg-white/15"
              data-testid={`folder-rename-${folder.folder_id}`}
              title="Nimeä uudelleen"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => removeFolder(folder.folder_id)}
              className="p-1 rounded hover:bg-red-500/30"
              data-testid={`folder-delete-${folder.folder_id}`}
              title="Poista"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </div>
        {adding === folder.folder_id && (
          <div className="flex items-center gap-1 my-1" style={{ paddingLeft: (depth + 1) * 10 + 24 }}>
            <input
              autoFocus value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitNew(); } if (e.key === "Escape") { setAdding(null); setNewName(""); } }}
              placeholder="Alikansion nimi"
              className="flex-1 px-2 py-1 text-sm rounded text-slate-900 border border-emerald-300 outline-none"
              data-testid="folder-new-input"
            />
            <button onClick={submitNew} className="p-1 rounded bg-white text-emerald-700"><Plus className="h-3 w-3" /></button>
            <button onClick={() => { setAdding(null); setNewName(""); }} className="p-1 rounded hover:bg-white/15"><X className="h-3 w-3" /></button>
          </div>
        )}
        {isOpen && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  const roots = tree.ROOT || [];

  return (
    <div className="min-h-screen bg-slate-50 md:flex print-app-shell">
      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-100 flex items-center justify-end px-4 h-14 print-hide" data-testid="appshell-mobile-bar">
        <button onClick={() => setOpen(true)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-700" data-testid="appshell-open-menu">
          <Menu className="h-5 w-5" />
        </button>
      </div>

      <aside
        className={`fixed md:sticky inset-y-0 left-0 z-40 md:z-10 md:top-0 md:h-screen w-80 brand-sidebar text-white flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        data-testid="appshell-sidebar"
      >
        <div className="px-6 pt-5 pb-2 flex items-center justify-end md:hidden">
          <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-white/10" data-testid="appshell-close-menu">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="px-4 mt-6 md:mt-8 flex-shrink-0">
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-white/50 px-3 mb-3">{t("nav.menu")}</div>
          <div className="space-y-1.5">
            {NAV_ITEMS.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors ${
                    isActive ? "bg-white text-emerald-700 shadow-sm" : "text-white/85 hover:bg-white/10"
                  }`
                }
                data-testid={`appshell-nav-${it.to.replace(/\W+/g, "-")}`}
              >
                <it.icon className="h-5 w-5" />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Folder tree section — hidden in new property-based UX, kept in DOM only for legacy filter state */}
        <div className="hidden" data-testid="sidebar-folders">
          <div className="flex items-center justify-between px-3 mb-1.5">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-white/50 inline-flex items-center gap-1.5">
              <Folder className="h-3 w-3" /> Asiakkaat / Kohteet
            </div>
            <button
              type="button"
              onClick={() => { setAdding("ROOT"); setNewName(""); }}
              className="p-1 rounded hover:bg-white/10 text-white/70"
              data-testid="folder-add-root"
              title="Uusi asiakas/yläkansio"
            >
              <FolderPlus className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="space-y-0.5 mb-1">
            <button
              type="button"
              onClick={() => goWithFolder("ALL")}
              className={`w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${
                (selectedId === "ALL" || !selectedId) && location.pathname === "/app"
                  ? "bg-white text-emerald-700 font-semibold shadow-sm"
                  : "text-white/85 hover:bg-white/10"
              }`}
              data-testid="folder-select-all"
            >
              <LayoutDashboard className="h-3.5 w-3.5" /> Kaikki
            </button>
            <button
              type="button"
              onClick={() => goWithFolder("UNFILED")}
              className={`w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${
                selectedId === "UNFILED" ? "bg-white text-emerald-700 font-semibold shadow-sm" : "text-white/85 hover:bg-white/10"
              }`}
              data-testid="folder-select-unfiled"
            >
              <Inbox className="h-3.5 w-3.5" /> Hallinnoimaton
            </button>
          </div>

          {adding === "ROOT" && (
            <div className="flex items-center gap-1 mt-1 mb-1 px-1">
              <input
                autoFocus value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitNew(); } if (e.key === "Escape") { setAdding(null); setNewName(""); } }}
                placeholder="esim. As Oy Kirjokuja"
                className="flex-1 px-2 py-1 text-sm rounded text-slate-900 border border-emerald-300 outline-none"
                data-testid="folder-new-input"
              />
              <button onClick={submitNew} className="p-1 rounded bg-white text-emerald-700" data-testid="folder-new-submit"><Plus className="h-3 w-3" /></button>
              <button onClick={() => { setAdding(null); setNewName(""); }} className="p-1 rounded hover:bg-white/15" data-testid="folder-new-cancel"><X className="h-3 w-3" /></button>
            </div>
          )}

          <div className="border-t border-white/10 pt-1">
            {roots.length === 0 && adding !== "ROOT" ? (
              <button
                type="button"
                onClick={() => { setAdding("ROOT"); setNewName(""); }}
                className="w-full text-center text-xs text-white/60 hover:text-white py-3 rounded-lg border border-dashed border-white/20 hover:border-white/40 transition-colors"
                data-testid="folder-empty-cta"
              >
                <FolderPlus className="h-3.5 w-3.5 inline mr-1" /> Luo ensimmäinen asiakas
              </button>
            ) : (
              roots.map((f) => renderNode(f, 0))
            )}
          </div>
        </div>

        <div className="p-4 border-t border-white/10 flex-shrink-0">
          <div className="px-3 py-3 flex items-center gap-3.5 min-w-0">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-11 w-11 rounded-full border border-white/20" />
            ) : (
              <div className="h-11 w-11 rounded-full bg-white/15 flex items-center justify-center text-white text-base font-bold">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold truncate" data-testid="appshell-user-name">{user?.name || t("nav.user")}</div>
              <div className="text-xs text-white/60 truncate">{user?.email}</div>
            </div>
          </div>
          <a
            href="mailto:info@veraso.fi"
            className="mt-2 w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-medium text-white/80 hover:bg-white/10"
            data-testid="appshell-support-email"
          >
            <Mail className="h-5 w-5" /> info@veraso.fi
          </a>
          <button onClick={onLogout} className="mt-2 w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[15px] font-medium text-white/85 hover:bg-white/10" data-testid="appshell-logout">
            <LogOut className="h-5 w-5" /> {t("nav.logout")}
          </button>

        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0 print-main">{children}</main>
      <PwaInstallPrompt />
    </div>
  );
}
